import Box from '@mui/material/Box';
import { useQuery } from '@tanstack/react-query';

import type { UserIdentity } from '@greco-gls/shared';

import { fetchIdentities } from '@/api/endpoints';
import { queryKeys } from '@/api/queryClient';

/** The server's cap per request (`MAX_IDENTITY_OIDS`). */
const MAX_PER_REQUEST = 200;

interface Waiter {
  resolve: (identity: UserIdentity | null) => void;
  reject: (error: unknown) => void;
}

let pending = new Map<string, Waiter[]>();
let scheduled = false;

/**
 * Every label a render asks for in the same tick goes out as one request: a history page of 50
 * rows is one `GET /access/identities`, not 50. The query cache then keeps each answer per oid.
 */
function loadIdentity(oid: string): Promise<UserIdentity | null> {
  return new Promise((resolve, reject) => {
    pending.set(oid, [...(pending.get(oid) ?? []), { resolve, reject }]);
    if (!scheduled) {
      scheduled = true;
      setTimeout(() => void flush(), 0);
    }
  });
}

async function flush(): Promise<void> {
  const batch = pending;
  pending = new Map();
  scheduled = false;

  const oids = [...batch.keys()];
  const chunks: string[][] = [];
  for (let start = 0; start < oids.length; start += MAX_PER_REQUEST) {
    chunks.push(oids.slice(start, start + MAX_PER_REQUEST));
  }
  await Promise.all(
    chunks.map(async (chunk) => {
      try {
        const found = new Map((await fetchIdentities(chunk)).map((item) => [item.oid, item]));
        for (const oid of chunk) batch.get(oid)?.forEach((w) => w.resolve(found.get(oid) ?? null));
      } catch (error) {
        for (const oid of chunk) batch.get(oid)?.forEach((w) => w.reject(error));
      }
    }),
  );
}

/** What an actor that is not a person reads as; `null` for an oid worth looking up. */
function machineLabel(actor: string): string | null {
  if (actor.startsWith('system:')) return `System (${actor.slice(7).replaceAll('_', ' ')})`;
  if (actor.startsWith('token:')) return `API token #${actor.slice(6)}`;
  return null;
}

/** `Name (email)`, or just the email when no name is known. */
export function formatIdentity(identity: UserIdentity): string {
  return identity.displayName ? `${identity.displayName} (${identity.email})` : identity.email;
}

/**
 * The person behind an actor oid (`createdBy`, `changedBy`, `decidedBy`, an activity actor).
 * Falls back to the raw value for anyone not in `app_users`, so a lookup failure never hides who
 * acted — the oid stays in the tooltip either way.
 */
export function UserLabel({ oid }: { oid: string }) {
  const machine = machineLabel(oid);
  const identity = useQuery({
    queryKey: queryKeys.identityFor(oid),
    queryFn: () => loadIdentity(oid),
    enabled: machine === null && oid !== '',
    // Names change about never; a missing one is not worth a toast.
    staleTime: 10 * 60_000,
    meta: { silent: true },
  });

  const text =
    machine ?? (identity.data ? formatIdentity(identity.data) : identity.isLoading ? '…' : oid);
  return (
    <Box component="span" title={oid}>
      {text}
    </Box>
  );
}
